import type { PreferredDesigner } from "@nlc/shared-types";

import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useListData } from "react-stately";

import { Avatar } from "@/components/avatar";
import { Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Select, SelectButton } from "@/components/select";
import { Tag } from "@/components/tag-group";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetPreferredDesigners } from "@/features/request/shared/UseGetPreferredDesigners.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

import { useEditRequestFormContext } from "../../hooks/useEditRequestForm";

export function PreferredDesignersSelect({
  className,
}: {
  className?: string;
}) {
  const projectId = useGetRequest()?.data?.projectId;
  const { data } = useGetPreferredDesigners(projectId);
  const { updateField } = useFieldUpdate();
  const methods = useEditRequestFormContext();

  const defaultItems: number[] = methods.watch("preferredDesignerIds") ?? [];

  const selectedList = useListData<PreferredDesigner>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    if (data) {
      selectedList.remove(...selectedList.items.map((item) => item.id));
      selectedList.append(
        ...data.filter((item) => defaultItems.includes(item.id)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItems, data]);

  if (data) {
    return (
      <Controller
        control={methods.control}
        name="preferredDesignerIds"
        render={({ field: { name, onBlur, onChange, value } }) => (
          <MultiSelectField className={className}>
            <Label>Preferred designers</Label>
            <MultiSelect
              items={data}
              name={name}
              onBlur={onBlur}
              onItemAdd={(key) => {
                const newValue = [...(value || []), key];

                onChange(newValue);
                void updateField(name, newValue);
              }}
              onItemRemove={(key) => {
                const newValue = (value || []).filter(
                  (id: number) => id !== key,
                );

                onChange(newValue);
                void updateField(name, newValue);
              }}
              placeholder="Select"
              renderEmptyState={(inputValue) => (
                <span className="p-2 text-text-primary">
                  {inputValue ? (
                    <>No results found for: {inputValue}</>
                  ) : (
                    `No designers available`
                  )}
                </span>
              )}
              selectedList={selectedList}
              tag={(item) => (
                <Tag textValue={String(item.id)}>
                  <Avatar
                    alt={item.textValue}
                    className="rounded-full size-3"
                    src={item.profilePicture}
                  />
                  {item.textValue}
                </Tag>
              )}
            >
              {(item) => (
                <MultiSelectItem textValue={String(item.id)}>
                  {item.textValue}
                </MultiSelectItem>
              )}
            </MultiSelect>
          </MultiSelectField>
        )}
      />
    );
  }

  return (
    <Select className={className} placeholder="Loading...">
      <Label>Preferred designers</Label>
      <SelectButton />
    </Select>
  );
}
